import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Autocomplete,
  InputAdornment,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useState, useEffect } from "react";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";

export interface IContact {
  id: number | string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

interface ContactSearchFieldProps {
  onSelect: (contact: IContact | null) => void;
  fieldSx?: object;
}

export default function ContactSearchField({
  onSelect,
  fieldSx,
}: ContactSearchFieldProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [contactOptions, setContactOptions] = useState<IContact[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [savingClient, setSavingClient] = useState(false);

  const defaultFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "30px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      overflow: "hidden",
      paddingRight: "10px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: `1px solid ${colors.primary[400]}`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 20px",
    },
  };

  const mergedFieldSx = fieldSx ?? defaultFieldSx;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (contactSearchTerm.length >= 3) fetchContacts(contactSearchTerm);
      else setContactOptions([]);
    }, 500);
    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  const fetchContacts = async (search: string) => {
    setContactLoading(true);
    try {
      const res = await apiService.get<{ results: IContact[] }>(
        `/api/contacts/contact/?search=${search}`
      );
      setContactOptions(res.data.results || []);
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
    } finally {
      setContactLoading(false);
    }
  };

  const handleSaveNewClient = async () => {
    setSavingClient(true);
    try {
      const res = await apiService.post<IContact>("/api/contacts/contact/", {
        ...newClient,
        contact_type: "individual",
      });
      const saved = res.data;
      setContactOptions((prev) => [...prev, saved]);
      setSelectedContact(saved);
      onSelect(saved);
      setAddClientOpen(false);
      setNewClient({ first_name: "", last_name: "", email: "", phone: "" });
    } catch (err) {
      console.error("Failed to save client:", err);
    } finally {
      setSavingClient(false);
    }
  };

  return (
    <>
      <Autocomplete
        fullWidth
        options={contactOptions}
        getOptionLabel={(option: IContact) =>
          `${option.first_name || ""} ${option.last_name || ""}`.trim()
        }
        value={selectedContact}
        onChange={(_e, value: IContact | null) => {
          setSelectedContact(value);
          onSelect(value);
        }}
        onInputChange={(_e, val) => setContactSearchTerm(val)}
        loading={contactLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search client..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {contactLoading ? (
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </InputAdornment>
              ),
            }}
            sx={mergedFieldSx}
          />
        )}
      />
      <Typography
        variant="caption"
        color={theme.palette.text.secondary}
        sx={{ pl: 1, mt: 0.5, display: "block" }}
      >
        Type at least 3 characters to search
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mt={1}
        onClick={() => setAddClientOpen(true)}
        sx={{ cursor: "pointer", pl: 1 }}
      >
        <AddCircleOutlineIcon sx={{ fontSize: 18, color: colors.primary[400] }} />
        <Typography fontSize={13} color={theme.palette.text.primary} fontWeight={500}>
          Add New Client
        </Typography>
      </Box>

      {/* Add Client Dialog */}
      <Dialog
        open={addClientOpen}
        onClose={() => setAddClientOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, backgroundColor: colors.primary[900] },
        }}
      >
        <IconButton
          onClick={() => setAddClientOpen(false)}
          size="small"
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "50%",
            width: 26,
            height: 26,
            color: theme.palette.text.primary,
            zIndex: 10,
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>

        <DialogContent sx={{ p: "24px" }}>
          <Typography variant="h6" fontWeight={600} color={colors.primary[400]} mb={3}>
            Add New Client
          </Typography>

          {[
            { label: "First Name", key: "first_name" },
            { label: "Last Name", key: "last_name" },
            { label: "Email", key: "email" },
            { label: "Phone", key: "phone" },
          ].map(({ label, key }) => (
            <Box key={key} mb={2}>
              <Typography variant="body2" color={theme.palette.text.secondary} mb={1}>
                {label}
              </Typography>
              <TextField
                fullWidth
                value={newClient[key as keyof typeof newClient]}
                onChange={(e) =>
                  setNewClient((p) => ({ ...p, [key]: e.target.value }))
                }
                sx={mergedFieldSx}
              />
            </Box>
          ))}

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <button
              onClick={handleSaveNewClient}
              disabled={savingClient || !newClient.first_name || !newClient.last_name}
              style={{
                background: colors.primary[400],
                color: colors.primary[900],
                border: "none",
                borderRadius: "12px",
                padding: "14px 28px",
                fontSize: 15,
                fontWeight: 500,
                cursor: savingClient ? "not-allowed" : "pointer",
                opacity: savingClient || !newClient.first_name || !newClient.last_name ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {savingClient && <CircularProgress size={16} color="inherit" />}
              {savingClient ? "Saving..." : "Save Client"}
            </button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}